import React from 'react'

/**
 * Assessment Status Indicator SVG icons — Figma-matched
 *
 * States:
 *   idle-outline  → tool on, no activity         → white bg + #C2D0D7 border, #637282 icon
 *   idle-filled   → tool on, others have acted   → #637282 solid bg, white icon
 *   active-filled → current user has acted       → #00C3FF solid bg, white icon
 *
 * Each icon renders centred at (0, 0) within r=4 (8 px diameter).
 * Callers wrap with <g transform="translate(x, y) scale(s)"> to position.
 *
 * Icon paths come directly from the 36×36 Figma artboards.
 * They are mapped to r=4 via:  scale(4/18) translate(-18,-18)
 */

const BORDER  = '#C2D0D7'
const GRAY    = '#637282'
const BLUE    = '#00C3FF'
const WHITE   = '#ffffff'

// Maps Figma 36×36 space (centre 18,18) → r=4 centered at (0,0)
const FT = 'scale(0.222222) translate(-18,-18)'

// ── Circle backgrounds ───────────────────────────────────────────────────────
const OutlineBg = () => (
  <g>
    <circle r={4} fill={WHITE} />
    <circle r={3.6} fill="none" stroke={BORDER} strokeWidth={0.8} />
  </g>
)
const GrayBg = () => <circle r={4} fill={GRAY} />
const BlueBg = () => <circle r={4} fill={BLUE} />

// ── Voting — thumb-up (Figma) ────────────────────────────────────────────────
const VoteIcon = ({ fill }) => (
  <g transform={FT}>
    <path d="M25.5 17.5C25.5 15.5969 24.9999 15.197 23.4999 15.1969H19.4999L19.9999 10.6969C20.1665 9.19693 18.4999 9.19695 17.9999 10.1969L15 15.5969V23.7969C15 23.7969 16.4999 24.197 18.4999 24.197C20.4999 24.197 22 24.197 23.5 23.5C25 22.803 25.5 19.5 25.5 17.5Z" fill={fill} />
    <path d="M11.5 23.7L14 23.8V15.6L11.5 15.7C10.5002 15.74 10.5 15.7 10.5 16.7V22.7C10.5 23.7 10.5008 23.66 11.5 23.7Z" fill={fill} />
  </g>
)

// ── Rating — bar-chart equaliser (Figma) ─────────────────────────────────────
const RateIcon = ({ fill }) => (
  <g transform={FT}>
    <rect x="10" y="22.5" width="2.5" height="16" rx="1.25" transform="rotate(-90 10 22.5)" fill={fill} />
    <rect x="15" y="23.5" width="4.5" height="1.5" transform="rotate(-90 15 23.5)" fill={fill} />
    <rect x="10" y="16"   width="2.5" height="16" rx="1.25" transform="rotate(-90 10 16)"   fill={fill} />
    <rect x="19.5" y="17" width="4.5" height="1.5" transform="rotate(-90 19.5 17)"           fill={fill} />
  </g>
)

// ── Commenting — speech bubble (Figma) ───────────────────────────────────────
const CommentIcon = ({ bubbleFill, dotFill }) => (
  <g transform={FT}>
    <rect x="11" y="11.5" width="15" height="10" rx="1" fill={bubbleFill} />
    <circle cx="15.5" cy="16.5" r="1" fill={dotFill} />
    <circle cx="18.5" cy="16.5" r="1" fill={dotFill} />
    <circle cx="21.5" cy="16.5" r="1" fill={dotFill} />
    <path d="M13 21.5005H17L14 26C13.4453 26.832 13 27 13 25.9999V21.5005Z" fill={bubbleFill} />
  </g>
)

// ── Public indicator components ──────────────────────────────────────────────

/**
 * VotingIndicator
 * @param {'idle-outline'|'idle-filled'|'active-filled'} state
 */
export const VotingIndicator = ({ state }) => {
  if (state === 'idle-outline')  return <g><OutlineBg /><VoteIcon fill={GRAY}  /></g>
  if (state === 'idle-filled')   return <g><GrayBg   /><VoteIcon fill={WHITE} /></g>
  if (state === 'active-filled') return <g><BlueBg   /><VoteIcon fill={WHITE} /></g>
  return null
}

/**
 * RatingIndicator
 * @param {'idle-outline'|'idle-filled'|'active-filled'} state
 */
export const RatingIndicator = ({ state }) => {
  if (state === 'idle-outline')  return <g><OutlineBg /><RateIcon fill={GRAY}  /></g>
  if (state === 'idle-filled')   return <g><GrayBg   /><RateIcon fill={WHITE} /></g>
  if (state === 'active-filled') return <g><BlueBg   /><RateIcon fill={WHITE} /></g>
  return null
}

/**
 * CommentingIndicator
 * @param {'idle-outline'|'idle-filled'|'active-filled'} state
 */
export const CommentingIndicator = ({ state }) => {
  if (state === 'idle-outline')  return <g><OutlineBg /><CommentIcon bubbleFill={GRAY}  dotFill={WHITE} /></g>
  if (state === 'idle-filled')   return <g><GrayBg   /><CommentIcon bubbleFill={WHITE} dotFill={GRAY}  /></g>
  if (state === 'active-filled') return <g><BlueBg   /><CommentIcon bubbleFill={WHITE} dotFill={BLUE}  /></g>
  return null
}
